import React, { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
 Search,
 RefreshCw,
 Plus,
 X,
 DollarSign,
 Building2,
 ExternalLink,
 ChevronRight,
 Wallet,
 ArrowDownLeft,
 CheckCircle2,
 FileText,
 AlertCircle,
 ArrowLeft,
 Filter,
 History,
 Trash2,
 ListChecks,
 Loader2,
 BookOpen,
 Clock,
 Percent,
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import {
 getAdminVendorPayoutVendors,
 getAdminVendorDisbursements,
 postAdminVendorDisbursement,
 postAdminVendorConnectTransfer,
 postAdminVendorPayoutFreeze,
 postAdminVendorConnectDisconnect,
 deleteAdminVendorDisbursement,
 getAdminAccountingOverview,
} from '../../services/vendorPayoutService';
import { formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { getPlatformSettlementCurrency } from '../../services/settlementCurrencyService';
import ModalShell from '../../components/shared/ModalShell';
import StripeConnectStatusBadge from '../../components/shared/StripeConnectStatusBadge';

const fmtMoney = (n, currency = 'USD') => {
 const v = Number(n);
 if (Number.isNaN(v)) return `${currency} 0.00`;
 try {
 return new Intl.NumberFormat(undefined, {
 style: 'currency',
 currency: currency || 'USD',
 }).format(v);
 } catch {
 return `${currency} ${v.toFixed(2)}`;
 }
};

const AdminVendorPayouts = () => {
 const queryClient = useQueryClient();
 const { success, error: showError } = useToastContext();
 const [searchParams, setSearchParams] = useSearchParams();
 const [settlementCurrency, setSettlementCurrency] = useState('USD');

 // Filters & Tabs
 const [activeFilterTab, setActiveFilterTab] = useState('due_only'); // 'due_only' | 'all'
 const [searchQuery, setSearchQuery] = useState('');
 const [sortBy, setSortBy] = useState('business_name:asc');
 const [vendorPage, setVendorPage] = useState(1);

 // Selected Vendor & History Pagination
 const [selectedVendorId, setSelectedVendorId] = useState(searchParams.get('vendor_id') || null);
 const [historyPage, setHistoryPage] = useState(1);
 const [historySearch, setHistorySearch] = useState('');

 // Mobile View state (show details on mobile when vendor selected)
 const [mobileShowDetails, setMobileShowDetails] = useState(false);

 // Modal State for Recording Payout
 const [payVendor, setPayVendor] = useState(null);
 const [payForm, setPayForm] = useState({
 amount: '',
 notes: '',
 booking_id: '',
 currency: 'USD',
 via_connect: false,
 });

 useEffect(() => {
 getPlatformSettlementCurrency()
 .then((data) => {
 if (!data?.currency) return;
 setSettlementCurrency(data.currency);
 setPayForm((f) => ({ ...f, currency: data.currency }));
 })
 .catch(() => {});
 }, []);

 // State for Delete Confirmation Modal
 const [deleteDisbursementTarget, setDeleteDisbursementTarget] = useState(null);

 // Query parameters for Vendor List
 const queryParams = useMemo(() => {
 const [sort_by, sort_order] = sortBy.split(':');
 return {
 page: vendorPage,
 per_page: 30,
 sort_by: sort_by || 'business_name',
 sort_order: sort_order || 'asc',
 due_only: activeFilterTab === 'due_only',
 ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
 };
 }, [vendorPage, sortBy, activeFilterTab, searchQuery]);

 const overviewQuery = useQuery({
 queryKey: ['admin-accounting-overview'],
 queryFn: async () => getAdminAccountingOverview(),
 });
 const platformOverview = overviewQuery.data?.data;

 // Fetch Vendors
 const {
 data: vendorsData,
 isLoading: vendorsLoading,
 error: vendorsError,
 refetch: refetchVendors,
 isFetching: vendorsFetching,
 } = useQuery({
 queryKey: ['admin-vendor-payout-vendors', queryParams],
 queryFn: async () => getAdminVendorPayoutVendors(queryParams),
 placeholderData: (prev) => prev,
 });

 const pagination = vendorsData?.data;
 const vendorList = useMemo(() => pagination?.data ?? [], [pagination?.data]);

 // Automatically select the first vendor if none selected or if selected vendor isn't in view
 const selectedVendor = useMemo(() => {
 if (!vendorList.length) return null;
 if (selectedVendorId) {
 const found = vendorList.find((v) => String(v.id) === String(selectedVendorId));
 if (found) return found;
 }
 return vendorList[0];
 }, [vendorList, selectedVendorId]);

 const displayCurrency =
 platformOverview?.currency || selectedVendor?.ledger?.currency || settlementCurrency;
 const money = (n) => fmtMoney(n, displayCurrency);

 // Keep search params in sync with selection
 useEffect(() => {
 if (selectedVendor && String(selectedVendor.id) !== selectedVendorId) {
 setSelectedVendorId(selectedVendor.id);
 }
 }, [selectedVendor, selectedVendorId]);

 // Fetch Disbursements for Selected Vendor
 const selectedVendorProfileId = selectedVendor?.id;
 const {
 data: disbursementsData,
 isLoading: historyLoading,
 error: historyError,
 refetch: refetchHistory,
 isFetching: historyFetching,
 } = useQuery({
 queryKey: ['admin-vendor-disbursements', selectedVendorProfileId, historyPage],
 queryFn: async () =>
 getAdminVendorDisbursements(selectedVendorProfileId, { page: historyPage, per_page: 15 }),
 enabled: Boolean(selectedVendorProfileId),
 placeholderData: (prev) => prev,
 });

 const historyPagination = disbursementsData?.data;
 const allDisbursements = useMemo(
 () => historyPagination?.data ?? [],
 [historyPagination?.data]
 );

 // Client-side filter for history search if needed
 const filteredDisbursements = useMemo(() => {
 if (!historySearch.trim()) return allDisbursements;
 const term = historySearch.toLowerCase();
 return allDisbursements.filter(
 (d) =>
 d.disbursement_reference?.toLowerCase().includes(term) ||
 d.notes?.toLowerCase().includes(term) ||
 d.booking?.booking_reference?.toLowerCase().includes(term) ||
 d.recorded_by?.name?.toLowerCase().includes(term) ||
 d.recorded_by?.email?.toLowerCase().includes(term)
 );
 }, [allDisbursements, historySearch]);

 // Mutation to Record Payout
 const recordMutation = useMutation({
 mutationFn: async () => {
 const amount = parseFloat(String(payForm.amount).replace(/,/g, ''), 10);
 if (Number.isNaN(amount) || amount <= 0) {
 throw new Error('Enter a valid amount greater than zero.');
 }
 const avail = Number(payVendor?.ledger?.available_balance || 0);
 if (avail <= 0) {
 throw new Error('This vendor has $0.00 available balance due to pay.');
 }
 if (amount > avail + 0.001) {
 throw new Error(
 `Amount cannot exceed the available balance due (${money(avail)}).`
 );
 }
 const payload = {
 amount,
 currency: (settlementCurrency || payForm.currency || 'USD').trim().toUpperCase(),
 notes: payForm.notes?.trim() || undefined,
 booking_id: payForm.booking_id?.trim() || undefined,
 };
 if (payForm.via_connect) {
 return postAdminVendorConnectTransfer(payVendor.id, {
 amount,
 notes: payload.notes,
 });
 }
 return postAdminVendorDisbursement(payVendor.id, payload);
 },
 onSuccess: (res) => {
 success(res?.message || 'Payout disbursement recorded successfully.');
 queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
 queryClient.invalidateQueries({ queryKey: ['admin-accounting-overview'] });
 queryClient.invalidateQueries({ queryKey: ['admin-accounting-statement'] });
 if (selectedVendorProfileId === payVendor?.id) {
 queryClient.invalidateQueries({
 queryKey: ['admin-vendor-disbursements', payVendor.id],
 });
 }
 setPayVendor(null);
 setPayForm({
 amount: '',
 notes: '',
 booking_id: '',
 currency: settlementCurrency,
 via_connect: false,
 });
 },
 onError: (err) => {
 showError(err?.message || 'Could not record disbursement.');
 },
 });

 // Mutation to Delete Disbursement
 const deleteMutation = useMutation({
 mutationFn: async (disbursementId) => {
 if (!selectedVendorProfileId) throw new Error('No vendor selected.');
 return deleteAdminVendorDisbursement(selectedVendorProfileId, disbursementId);
 },
 onSuccess: (res) => {
 success(res?.message || 'Disbursement record deleted.');
 queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
 queryClient.invalidateQueries({ queryKey: ['admin-accounting-overview'] });
 queryClient.invalidateQueries({ queryKey: ['admin-accounting-statement'] });
 if (selectedVendorProfileId) {
 queryClient.invalidateQueries({
 queryKey: ['admin-vendor-disbursements', selectedVendorProfileId],
 });
 }
 setDeleteDisbursementTarget(null);
 },
 onError: (err) => {
 showError(err?.message || 'Could not delete disbursement.');
 },
 });

 const freezeMutation = useMutation({
 mutationFn: async ({ vendorId, frozen, reason }) =>
 postAdminVendorPayoutFreeze(vendorId, { frozen, reason }),
 onSuccess: (res) => {
 success(res?.message || 'Payout freeze updated.');
 queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
 },
 onError: (err) => showError(err?.message || 'Could not update payout freeze.'),
 });

 const connectDisconnectMutation = useMutation({
 mutationFn: async (vendorId) =>
 postAdminVendorConnectDisconnect(vendorId, { delete_remote: true }),
 onSuccess: (res) => {
 success(res?.message || 'Stripe Connect unlinked.');
 queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
 },
 onError: (err) => showError(err?.message || 'Could not unlink Connect account.'),
 });

 const handleOpenPayModal = (vendor) => {
 const target = vendor || selectedVendor;
 if (!target) return;
 const avail = target.ledger?.available_balance ?? 0;
 setPayVendor(target);
 setPayForm({
 amount: avail > 0 ? String(avail.toFixed(2)) : '',
 notes: '',
 booking_id: '',
 currency: settlementCurrency,
 via_connect: false,
 });
 };

 const handleSelectVendor = (v) => {
 setSelectedVendorId(v.id);
 setSearchParams({ vendor_id: v.id });
 setHistoryPage(1);
 setHistorySearch('');
 setMobileShowDetails(true);
 };

 const parsedPayAmount = parseFloat(String(payForm.amount).replace(/,/g, ''));
 const payAvail = Number(payVendor?.ledger?.available_balance || 0);
 const isPayAmountOverdue =
 !Number.isNaN(parsedPayAmount) && parsedPayAmount > payAvail + 0.001;
 const isPayDisabled =
 recordMutation.isPending ||
 payAvail <= 0 ||
 Number.isNaN(parsedPayAmount) ||
 parsedPayAmount <= 0 ||
 isPayAmountOverdue;

 return (
 <div className="space-y-5 max-w-[1600px] mx-auto pb-12">
 <AdminPageHeader
 title="Accounts payable · Vendor payouts"
 subtitle="Settle vendor ledgers: see balances due after platform fees, then record each disbursement when money leaves the platform."
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Link
 to={
 selectedVendor
 ? `/admin/accounting?vendor_id=${selectedVendor.id}`
 : '/admin/accounting'
 }
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm whitespace-nowrap"
 >
 <BookOpen className="w-4 h-4 text-ink" />
 Open ledger
 </Link>
 <button
 type="button"
 onClick={() => {
 refetchVendors();
 overviewQuery.refetch();
 if (selectedVendorProfileId) refetchHistory();
 }}
 disabled={vendorsFetching || historyFetching || overviewQuery.isFetching}
 className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm disabled:opacity-50 transition-colors whitespace-nowrap"
 >
 <RefreshCw
 className={`w-4 h-4 mr-2 text-gray-500 ${ vendorsFetching || historyFetching || overviewQuery.isFetching ? 'animate-spin' : '' }`}
 />
 Refresh
 </button>
 {selectedVendor && (
 <button
 type="button"
 onClick={() => handleOpenPayModal(selectedVendor)}
 disabled={(selectedVendor.ledger?.available_balance ?? 0) <= 0}
 className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ink text-white text-sm font-medium hover:bg-black shadow-sm transition-colors disabled:opacity-40 whitespace-nowrap"
 title={
 (selectedVendor.ledger?.available_balance ?? 0) <= 0
 ? Number(selectedVendor.ledger?.held_from_incomplete_events || 0) > 0
 ? 'Earnings are held until events are marked completed'
 : 'All payable earnings for this vendor have been settled'
 : 'Record payout disbursement'
 }
 >
 <Plus className="w-4 h-4" />
 Record payout
 </button>
 )}
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendorPayouts.title}
 steps={ADMIN_PAGE_GUIDES.vendorPayouts.steps}
 icon={ListChecks}
 />

 <div className="rounded-lg border border-border bg-gradient-to-r from-amber-50/70 via-white to-surface/70 px-4 py-3 text-sm text-ink-secondary">
 <span className="font-medium text-ink">Settlement desk: </span>
 Only earnings from <strong className="font-semibold text-ink">completed events</strong> are
 payable. Paid bookings still in progress stay held until the vendor marks the event complete. Confirm the amount
 matches what you sent outside the app, then record the disbursement. Full credit/debit history lives on{' '}
 <Link to="/admin/accounting" className="text-ink font-medium hover:underline">
 Accounting
 </Link>
 .
 </div>

 {/* Platform settlement KPIs */}
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
 <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
 Accounts payable
 </span>
 <div className="p-2 rounded-xl bg-amber-50 text-amber-700">
 <Wallet className="w-5 h-5" />
 </div>
 </div>
 <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
 {overviewQuery.isLoading ? '…' : money(platformOverview?.vendor_balance_due)}
 </p>
 <p className="text-xs text-ink-tertiary mt-1">Total balance still due</p>
 </div>

 <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold uppercase tracking-wider text-ink">
 Disbursements sent
 </span>
 <div className="p-2 rounded-xl bg-surface-muted text-ink">
 <ArrowDownLeft className="w-5 h-5" />
 </div>
 </div>
 <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
 {overviewQuery.isLoading
 ? '…'
 : money(platformOverview?.vendor_disbursements_sent)}
 </p>
 <p className="text-xs text-ink-tertiary mt-1">Lifetime recorded payouts</p>
 </div>

 <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
 Vendors due
 </span>
 <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700">
 <Building2 className="w-5 h-5" />
 </div>
 </div>
 <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
 {overviewQuery.isLoading ? '…' : platformOverview?.vendors_with_balance_due ?? 0}
 </p>
 <p className="text-xs text-ink-tertiary mt-1">With balance &gt; $0</p>
 </div>

 <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold uppercase tracking-wider text-ink">
 Platform fees
 </span>
 <div className="p-2 rounded-xl bg-surface-muted text-ink">
 <Percent className="w-5 h-5" />
 </div>
 </div>
 <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
 {overviewQuery.isLoading
 ? '…'
 : money(platformOverview?.platform_fees_collected)}
 </p>
 <p className="text-xs text-ink-tertiary mt-1">Retained commission</p>
 </div>

 <div className="bg-surface rounded-lg border border-border p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold uppercase tracking-wider text-ink-secondary">
 Selected account
 </span>
 <div className="p-2 rounded-xl bg-gray-100 text-ink-secondary">
 <DollarSign className="w-5 h-5" />
 </div>
 </div>
 <p className="text-2xl font-bold text-ink mt-2 tabular-nums">
 {selectedVendor ? money(selectedVendor.ledger?.available_balance) : '—'}
 </p>
 <p className="text-xs text-ink-tertiary mt-1 truncate">
 {selectedVendor ? selectedVendor.business_name : 'No vendor selected'}
 </p>
 </div>
 </div>

 {/* Main Two-Pane Workspace Layout */}
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
 {/* LEFT COLUMN: Vendor Directory List */}
 <div
 className={`lg:col-span-4 xl:col-span-3 bg-surface rounded-lg border border-border shadow-sm overflow-hidden flex flex-col ${ mobileShowDetails ? 'hidden lg:flex' : 'flex' }`}
 >
 {/* List Controls & Search */}
 <div className="p-3.5 border-b border-border space-y-2.5 bg-surface-muted">
 {/* Filter Tabs */}
 <div className="flex p-1 bg-gray-100 rounded-xl border border-border">
 <button
 type="button"
 onClick={() => {
 setActiveFilterTab('due_only');
 setVendorPage(1);
 }}
 className={`flex-1 py-1 text-xs font-semibold rounded-lg transition-all ${ activeFilterTab === 'due_only' ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 Due to Pay
 </button>
 <button
 type="button"
 onClick={() => {
 setActiveFilterTab('all');
 setVendorPage(1);
 }}
 className={`flex-1 py-1 text-xs font-semibold rounded-lg transition-all ${ activeFilterTab === 'all' ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 All Vendors
 </button>
 </div>

 {/* Search Input */}
 <div className="relative">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
 <input
 type="search"
 value={searchQuery}
 onChange={(e) => {
 setSearchQuery(e.target.value);
 setVendorPage(1);
 }}
 placeholder="Search vendor…"
 className="w-full pl-8 pr-3 py-1.5 bg-surface border border-border rounded-xl text-xs text-ink focus:outline-none focus:ring-2 focus:ring-ink focus:border-ink transition-all placeholder:text-ink-tertiary"
 />
 </div>

 {/* Sort Dropdown */}
 <div className="flex items-center justify-between text-xs text-ink-tertiary px-0.5">
 <span>{vendorList.length} vendors</span>
 <div className="flex items-center gap-1.5">
 <Filter className="w-3.5 h-3.5 text-gray-400" />
 <select
 value={sortBy}
 onChange={(e) => {
 setSortBy(e.target.value);
 setVendorPage(1);
 }}
 className="bg-transparent border-0 text-xs font-medium text-ink-secondary focus:ring-0 cursor-pointer p-0"
 >
 <option value="business_name:asc">Name (A–Z)</option>
 <option value="business_name:desc">Name (Z–A)</option>
 <option value="created_at:desc">Newest</option>
 <option value="created_at:asc">Oldest</option>
 </select>
 </div>
 </div>
 </div>

 {/* Vendors Scrollable List */}
 <div className="divide-y divide-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
 {vendorsLoading && !vendorList.length ? (
 <div className="py-12 text-center space-y-2">
 <Loader2 className="w-7 h-7 animate-spin text-ink mx-auto" />
 <p className="text-xs text-ink-tertiary">Loading vendors…</p>
 </div>
 ) : vendorsError ? (
 <div className="p-4 text-center text-xs text-red-600 space-y-2">
 <AlertCircle className="w-5 h-5 mx-auto text-red-500" />
 <p>{vendorsError.message || 'Failed to load vendors.'}</p>
 </div>
 ) : vendorList.length === 0 ? (
 <div className="p-6 text-center text-gray-500 space-y-2">
 <Building2 className="w-7 h-7 mx-auto text-gray-300" />
 <p className="text-xs font-medium text-ink-secondary">No vendors found</p>
 <p className="text-[11px] text-gray-400">
 {activeFilterTab === 'due_only'
 ? 'No vendors currently have an outstanding payout balance.'
 : 'No vendors match your search.'}
 </p>
 </div>
 ) : (
 vendorList.map((vendor) => {
 const isSelected = selectedVendor?.id === vendor.id;
 const avail = Number(vendor.ledger?.available_balance || 0);
 const disbursed = Number(vendor.ledger?.total_disbursed || 0);

 return (
 <button
 key={vendor.id}
 type="button"
 onClick={() => handleSelectVendor(vendor)}
 className={`w-full text-left p-3.5 transition-all flex items-start gap-2.5 relative ${ isSelected ? 'bg-surface-muted/50 border-l-4 border-ink font-medium' : 'hover:bg-surface-muted/50 border-l-4 border-transparent' }`}
 >
 {/* Avatar / Initials */}
 <div
 className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 shadow-sm ${ isSelected ? 'bg-ink text-white' : 'bg-gray-100 text-ink-secondary border border-border' }`}
 >
 {(vendor.business_name || 'V').charAt(0).toUpperCase()}
 </div>

 {/* Vendor Info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-1.5">
 <p className="text-xs sm:text-sm font-semibold text-ink truncate">
 {vendor.business_name || 'Unnamed Vendor'}
 </p>
 {avail > 0 ? (
 <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900">
 {money(avail)}
 </span>
 ) : (
 <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
 Settled
 </span>
 )}
 </div>

 <p className="text-[11px] text-ink-tertiary truncate mt-0.5">
 {vendor.user?.email || 'No email attached'}
 </p>

 <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100/80 text-[11px] text-ink-tertiary">
 <span>
 Sent: <strong className="text-ink-secondary">{money(disbursed)}</strong>
 </span>
 {vendor.stripe_connect?.account_id ? (
 <StripeConnectStatusBadge
 connect={vendor.stripe_connect}
 showPayoutReady={false}
 />
 ) : (
 <span className="flex items-center text-[11px] font-medium text-ink">
 View
 <ChevronRight className="w-3 h-3 ml-0.5 text-gray-400" />
 </span>
 )}
 </div>
 </div>
 </button>
 );
 })
 )}
 </div>

 {/* List Pagination */}
 {pagination && pagination.last_page > 1 && (
 <div className="p-2.5 border-t border-border flex items-center justify-between text-xs bg-surface-muted">
 <span className="text-[11px] text-ink-tertiary">
 {pagination.current_page} / {pagination.last_page}
 </span>
 <div className="flex gap-1">
 <button
 type="button"
 disabled={pagination.current_page <= 1}
 onClick={() => setVendorPage((p) => Math.max(1, p - 1))}
 className="px-2 py-0.5 rounded border border-border bg-surface text-ink-secondary text-xs disabled:opacity-40 hover:bg-surface-muted"
 >
 Prev
 </button>
 <button
 type="button"
 disabled={pagination.current_page >= pagination.last_page}
 onClick={() => setVendorPage((p) => p + 1)}
 className="px-2 py-0.5 rounded border border-border bg-surface text-ink-secondary text-xs disabled:opacity-40 hover:bg-surface-muted"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>

 {/* RIGHT COLUMN: Vendor Payout Details & Payment History */}
 <div
 className={`lg:col-span-8 xl:col-span-9 space-y-5 ${ !mobileShowDetails ? 'hidden lg:block' : 'block' }`}
 >
 {/* Mobile Back Button */}
 <div className="lg:hidden mb-2">
 <button
 type="button"
 onClick={() => setMobileShowDetails(false)}
 className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-secondary hover:text-ink py-1 px-2.5 rounded-lg bg-gray-100"
 >
 <ArrowLeft className="w-3.5 h-3.5" />
 Back to vendors list
 </button>
 </div>

 {selectedVendor ? (
 <div className="space-y-5">
 {/* Selected Vendor Profile Banner */}
 <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-100">
 <div className="flex items-center gap-4">
 <div className="w-14 h-14 rounded-lg bg-ink text-white flex items-center justify-center font-bold text-xl shadow-md">
 {(selectedVendor.business_name || 'V').charAt(0).toUpperCase()}
 </div>
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-xl font-bold text-ink">
 {selectedVendor.business_name || 'Unnamed Vendor'}
 </h2>
 {selectedVendor.payouts_frozen && (
 <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-100 text-red-800">
 Payouts frozen
 </span>
 )}
 {selectedVendor.user_id && (
 <Link
 to={`/admin/vendors/${selectedVendor.user_id || selectedVendor.user?.id}`}
 className="text-xs text-ink-tertiary hover:text-gray-900 inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded-md transition-colors"
 title="View vendor management profile"
 >
 <span>Profile</span>
 <ExternalLink className="w-3 h-3" />
 </Link>
 )}
 </div>
 <p className="text-sm text-ink-tertiary mt-0.5">
 {selectedVendor.user?.email || 'No email associated'}
 {selectedVendor.payouts_frozen && selectedVendor.payouts_frozen_reason
 ? ` · ${selectedVendor.payouts_frozen_reason}`
 : ''}
 </p>
 </div>
 </div>

 <div className="flex flex-wrap items-center gap-2.5">
 <button
 type="button"
 disabled={freezeMutation.isPending}
 onClick={() => {
 const frozen = !selectedVendor.payouts_frozen;
 const reason = frozen
 ? window.prompt('Freeze reason (shown in audit trail):', 'Admin payout freeze')
 : null;
 if (frozen && reason === null) return;
 freezeMutation.mutate({
 vendorId: selectedVendor.id,
 frozen,
 reason: frozen ? reason || 'Admin payout freeze' : undefined,
 });
 }}
 className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm font-semibold shadow-sm transition-all disabled:opacity-50 ${ selectedVendor.payouts_frozen ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100' }`}
 >
 {selectedVendor.payouts_frozen ? 'Unfreeze payouts' : 'Freeze payouts'}
 </button>
 <Link
 to={`/admin/accounting?vendor_id=${selectedVendor.id}`}
 className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border bg-surface text-sm font-semibold text-ink-secondary hover:bg-surface-muted shadow-sm"
 >
 <BookOpen className="w-4 h-4 text-ink" />
 Ledger statement
 </Link>
 <button
 type="button"
 onClick={() => handleOpenPayModal(selectedVendor)}
 disabled={
 selectedVendor.payouts_frozen ||
 (selectedVendor.ledger?.available_balance ?? 0) <= 0
 }
 className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-ink text-white text-sm font-semibold hover:bg-black shadow-sm transition-all disabled:opacity-40"
 >
 <Plus className="w-4 h-4" />
 Record payout
 </button>
 </div>
 </div>

 <div className="mt-5 rounded-xl border border-border bg-surface-muted p-4">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div>
 <p className="text-xs font-semibold uppercase tracking-wider text-ink">
 Stripe Connect
 </p>
 {selectedVendor.stripe_connect?.account_id ? (
 <div className="mt-1 space-y-1 text-sm text-ink-secondary">
 <p>
 <span className="text-gray-500">Account:</span>{' '}
 <span className="font-mono text-xs">{selectedVendor.stripe_connect.account_id}</span>
 </p>
 <p className="capitalize">
 <span className="text-gray-500">Status:</span>{' '}
 {(selectedVendor.stripe_connect.onboarding_status || 'pending').replace(/_/g, ' ')}
 {' · '}
 Payout ready:{' '}
 {selectedVendor.stripe_connect.is_payout_ready ? 'Yes' : 'No'}
 </p>
 </div>
 ) : (
 <p className="mt-1 text-sm text-ink-secondary">
 No Connect account linked for this vendor.
 </p>
 )}
 </div>
 {selectedVendor.stripe_connect?.account_id ? (
 <button
 type="button"
 disabled={connectDisconnectMutation.isPending}
 onClick={() => {
 if (
 !window.confirm(
 'Unlink and delete this vendor’s Stripe Connect account so they can start over?'
 )
 ) {
 return;
 }
 connectDisconnectMutation.mutate(selectedVendor.id);
 }}
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink hover:bg-surface-muted disabled:opacity-50"
 >
 {connectDisconnectMutation.isPending ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <Trash2 className="w-4 h-4" />
 )}
 Reset Connect
 </button>
 ) : null}
 </div>
 </div>

 {/* Ledger Financial Summary Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 pt-6">
 <div className="p-4 rounded-xl bg-surface-muted border border-border">
 <div className="flex items-center justify-between text-xs font-semibold text-ink-tertiary uppercase tracking-wider">
 <span>Payable earned</span>
 <DollarSign className="w-4 h-4 text-gray-400" />
 </div>
 <p className="text-2xl font-bold text-ink mt-1.5">
 {money(selectedVendor.ledger?.total_earned_from_orders)}
 </p>
 <p className="text-xs text-ink-tertiary mt-1">
 From completed events only
 </p>
 </div>

 <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
 <div className="flex items-center justify-between text-xs font-semibold text-slate-600 uppercase tracking-wider">
 <span>Held</span>
 <Clock className="w-4 h-4 text-slate-500" />
 </div>
 <p className="text-2xl font-bold text-slate-900 mt-1.5">
 {money(selectedVendor.ledger?.held_from_incomplete_events)}
 </p>
 <p className="text-xs text-slate-600/80 mt-1">
 Paid but event not completed yet
 </p>
 </div>

 <div className="p-4 rounded-xl bg-surface-muted/70 border border-border/80">
 <div className="flex items-center justify-between text-xs font-semibold text-ink uppercase tracking-wider">
 <span>Total Sent (Disbursed)</span>
 <ArrowDownLeft className="w-4 h-4 text-ink" />
 </div>
 <p className="text-2xl font-bold text-ink mt-1.5">
 {money(selectedVendor.ledger?.total_disbursed)}
 </p>
 <p className="text-xs text-ink/80 mt-1">
 Total manual payouts recorded
 </p>
 </div>

 <div
 className={`p-4 rounded-xl border ${ (selectedVendor.ledger?.available_balance ?? 0) > 0 ? 'bg-amber-50/70 border-amber-200' : 'bg-emerald-50/70 border-emerald-200' }`}
 >
 <div
 className={`flex items-center justify-between text-xs font-semibold uppercase tracking-wider ${ (selectedVendor.ledger?.available_balance ?? 0) > 0 ? 'text-amber-700' : 'text-emerald-700' }`}
 >
 <span>
 {(selectedVendor.ledger?.available_balance ?? 0) > 0
 ? 'Balance Due to Pay'
 : 'Settlement Status'}
 </span>
 {(selectedVendor.ledger?.available_balance ?? 0) > 0 ? (
 <Wallet className="w-4 h-4 text-amber-600" />
 ) : (
 <CheckCircle2 className="w-4 h-4 text-emerald-600" />
 )}
 </div>
 <p
 className={`text-2xl font-bold mt-1.5 ${ (selectedVendor.ledger?.available_balance ?? 0) > 0 ? 'text-amber-900' : 'text-emerald-900' }`}
 >
 {money(selectedVendor.ledger?.available_balance)}
 </p>
 <p
 className={`text-xs mt-1 ${ (selectedVendor.ledger?.available_balance ?? 0) > 0 ? 'text-amber-700' : 'text-emerald-700' }`}
 >
 {(selectedVendor.ledger?.available_balance ?? 0) > 0
 ? 'Available to record and send'
 : Number(selectedVendor.ledger?.held_from_incomplete_events || 0) > 0
 ? 'Nothing payable until events complete'
 : 'All payable earnings settled'}
 </p>
 </div>
 </div>
 </div>

 {/* Disbursements / Payment History Card */}
 <div className="bg-surface rounded-lg border border-border shadow-sm overflow-hidden">
 {/* Section Header */}
 <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="flex items-center gap-2.5">
 <div className="p-2 bg-gray-100 text-ink-secondary rounded-xl">
 <History className="w-5 h-5" />
 </div>
 <div>
 <h3 className="text-base font-bold text-ink">
 Disbursement Payment History
 </h3>
 <p className="text-xs text-ink-tertiary mt-0.5">
 Historical list of payout records issued to {selectedVendor.business_name}
 </p>
 </div>
 </div>

 {/* Search / Filter for History */}
 <div className="relative w-full sm:w-64">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
 <input
 type="search"
 value={historySearch}
 onChange={(e) => setHistorySearch(e.target.value)}
 placeholder="Filter history by ref, booking…"
 className="w-full pl-8 pr-3 py-1.5 bg-surface-muted border border-border rounded-lg text-xs text-ink focus:outline-none focus:ring-1 focus:ring-ink"
 />
 </div>
 </div>

 {/* History Table */}
 <div className="overflow-x-auto">
 {historyLoading ? (
 <div className="py-16 text-center space-y-3">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto" />
 <p className="text-xs text-ink-tertiary">Loading disbursement history…</p>
 </div>
 ) : historyError ? (
 <div className="p-8 text-center text-xs text-red-600">
 {historyError.message || 'Failed to load disbursements.'}
 </div>
 ) : filteredDisbursements.length === 0 ? (
 <div className="py-14 text-center px-4 space-y-3">
 <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto">
 <FileText className="w-6 h-6" />
 </div>
 <p className="text-sm font-semibold text-ink">No disbursements recorded yet</p>
 <p className="text-xs text-ink-tertiary max-w-sm mx-auto">
 When you pay out earnings to this vendor outside the platform, record it here so the vendor can track their payouts.
 </p>
 {(selectedVendor.ledger?.available_balance ?? 0) > 0 && (
 <button
 type="button"
 onClick={() => handleOpenPayModal(selectedVendor)}
 className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-ink text-white text-xs font-semibold hover:bg-black transition-colors"
 >
 <Plus className="w-3.5 h-3.5" />
 Record First Payout
 </button>
 )}
 </div>
 ) : (
 <table className="min-w-full divide-y divide-gray-100 text-left text-xs sm:text-sm">
 <thead className="bg-surface-muted/70 text-ink-secondary font-semibold text-xs">
 <tr>
 <th className="px-5 py-3">Reference #</th>
 <th className="px-5 py-3">Amount</th>
 <th className="px-5 py-3">Date & Time</th>
 <th className="px-5 py-3">Linked Booking</th>
 <th className="px-5 py-3">Notes</th>
 <th className="px-5 py-3">Recorded By</th>
 <th className="px-5 py-3 text-right">Action</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100 bg-surface">
 {filteredDisbursements.map((item) => (
 <tr key={item.id} className="hover:bg-surface-muted/80 transition-colors">
 <td className="px-5 py-3.5 font-mono text-xs font-bold text-ink whitespace-nowrap">
 {item.disbursement_reference}
 </td>
 <td className="px-5 py-3.5 whitespace-nowrap">
 <span className="inline-flex items-center px-2.5 py-1 rounded-md font-bold text-xs bg-emerald-50 text-emerald-800 border border-emerald-100">
 {money(item.amount)}
 </span>
 </td>
 <td className="px-5 py-3.5 text-ink-secondary whitespace-nowrap text-xs">
 {formatDateTime(item.created_at)}
 </td>
 <td className="px-5 py-3.5 whitespace-nowrap text-xs">
 {item.booking?.booking_reference ? (
 <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 font-mono text-ink-secondary">
 #{item.booking.booking_reference}
 </span>
 ) : (
 <span className="text-gray-400">—</span>
 )}
 </td>
 <td className="px-5 py-3.5 text-ink-secondary text-xs max-w-xs truncate">
 {item.notes || <span className="text-gray-400">—</span>}
 </td>
 <td className="px-5 py-3.5 text-ink-tertiary text-xs whitespace-nowrap">
 {item.recorded_by?.name || item.recorded_by?.email || 'Admin'}
 </td>
 <td className="px-5 py-3.5 text-right whitespace-nowrap">
 <button
 type="button"
 onClick={() => setDeleteDisbursementTarget(item)}
 className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-100 transition-colors"
 title="Delete this payout record"
 >
 <Trash2 className="w-3.5 h-3.5" />
 <span>Delete</span>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>

 {/* History Pagination */}
 {historyPagination && historyPagination.last_page > 1 && (
 <div className="p-4 border-t border-border flex items-center justify-between text-xs bg-surface-muted">
 <span className="text-gray-600">
 Showing page {historyPagination.current_page} of {historyPagination.last_page} (
 {historyPagination.total} total payments)
 </span>
 <div className="flex gap-2">
 <button
 type="button"
 disabled={historyPage <= 1}
 onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
 className="px-3 py-1.5 rounded-lg border border-border bg-surface text-ink-secondary text-xs font-medium disabled:opacity-40 hover:bg-surface-muted"
 >
 Previous
 </button>
 <button
 type="button"
 disabled={historyPage >= historyPagination.last_page}
 onClick={() => setHistoryPage((p) => p + 1)}
 className="px-3 py-1.5 rounded-lg border border-border bg-surface text-ink-secondary text-xs font-medium disabled:opacity-40 hover:bg-surface-muted"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 ) : (
 <div className="bg-surface rounded-lg border border-border p-12 text-center shadow-sm space-y-3">
 <Building2 className="w-12 h-12 text-gray-300 mx-auto" />
 <h3 className="text-base font-bold text-ink">Select a vendor from the list</h3>
 <p className="text-xs text-ink-tertiary max-w-sm mx-auto">
 Choose a vendor on the left to view their order earnings ledger, available due balance, and complete disbursement history.
 </p>
 </div>
 )}
 </div>
 </div>

 {/* Record Payout Dialog Modal */}
 <ModalShell
 isOpen={!!payVendor}
 onClose={() => {
 if (!recordMutation.isPending) setPayVendor(null);
 }}
 labelledBy="record-payout-title"
 panelClassName="max-w-lg"
 >
 {payVendor ? (
 <div className="bg-surface rounded-lg shadow-sm border border-border w-full p-6 space-y-5">
 <div className="flex items-start justify-between gap-3 border-b border-border pb-4">
 <div className="flex items-center gap-3">
 <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-100">
 <DollarSign className="w-6 h-6" />
 </div>
 <div>
 <h2
 id="record-payout-title"
 className="text-lg font-bold text-ink"
 >
 Record Payout Disbursement
 </h2>
 <p className="text-xs text-ink-tertiary mt-0.5">
 For <strong className="text-ink">{payVendor.business_name}</strong>
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={() => !recordMutation.isPending && setPayVendor(null)}
 className="p-1.5 rounded-lg hover:bg-surface-muted text-gray-500 transition-colors"
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 <div className="bg-surface-muted rounded-xl p-3.5 border border-border flex items-center justify-between gap-3 text-xs">
 <div>
 <span className="text-gray-500">Available Balance Due:</span>
 <p className="text-base font-bold text-emerald-700">
 {money(payVendor.ledger?.available_balance)}
 </p>
 </div>
 <div className="text-right">
 <span className="text-gray-500">Held (incomplete events):</span>
 <p className="text-base font-semibold text-slate-700">
 {money(payVendor.ledger?.held_from_incomplete_events)}
 </p>
 </div>
 </div>

 {payAvail <= 0 && (
 <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
 <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
 <span>
 {Number(payVendor.ledger?.held_from_incomplete_events || 0) > 0 ? (
 <>
 Nothing is payable yet. <strong>{money(payVendor.ledger?.held_from_incomplete_events)}</strong> is
 held until those events are marked completed.
 </>
 ) : (
 <>
 This vendor has <strong>$0.00</strong> available balance due. All payable earnings from completed
 events have already been disbursed.
 </>
 )}
 </span>
 </div>
 )}

 <form
 onSubmit={(e) => {
 e.preventDefault();
 recordMutation.mutate();
 }}
 className="space-y-4"
 >
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <label className="block text-xs font-bold text-ink-secondary uppercase tracking-wider">
 Payout Amount ($) *
 </label>
 {payAvail > 0 && (
 <button
 type="button"
 onClick={() =>
 setPayForm((f) => ({ ...f, amount: String(payAvail.toFixed(2)) }))
 }
 className="text-xs text-ink hover:text-ink font-semibold hover:underline"
 >
 Fill Max ({money(payAvail)})
 </button>
 )}
 </div>
 <div className="relative">
 <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
 <input
 type="text"
 inputMode="decimal"
 required
 disabled={payAvail <= 0}
 value={payForm.amount}
 onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
 className={`w-full pl-8 pr-3 py-2.5 border rounded-xl text-sm font-semibold text-ink focus:ring-2 focus:ring-ink focus:border-ink ${ isPayAmountOverdue ? 'border-red-400 bg-red-50/40 text-red-900' : 'border-gray-300' }`}
 placeholder="0.00"
 />
 </div>
 {isPayAmountOverdue && (
 <p className="text-xs text-red-600 font-medium mt-1.5 flex items-center gap-1">
 <AlertCircle className="w-3.5 h-3.5" />
 Amount cannot exceed available balance due ({money(payAvail)}).
 </p>
 )}
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-bold text-ink-secondary uppercase tracking-wider mb-1.5">
 Currency
 </label>
 <input
 type="text"
 maxLength={3}
 value={settlementCurrency}
 readOnly
 disabled
 className="w-full px-3 py-2 border border-border rounded-xl text-sm uppercase bg-surface-muted text-ink"
 title="Locked to platform settlement currency"
 />
 <p className="mt-1 text-[11px] text-gray-500">
 From Settings → Settlement
 </p>
 </div>

 <div>
 <label className="block text-xs font-bold text-ink-secondary uppercase tracking-wider mb-1.5">
 Booking Reference / ID (Optional)
 </label>
 <input
 type="text"
 value={payForm.booking_id}
 disabled={!!payForm.via_connect}
 onChange={(e) => setPayForm((f) => ({ ...f, booking_id: e.target.value }))}
 className="w-full px-3 py-2 border border-border rounded-xl font-mono text-xs bg-surface text-ink disabled:opacity-50"
 placeholder="e.g. BK-2B9FTW or UUID"
 />
 </div>
 </div>

 <label className="flex items-start gap-3 rounded-xl border border-border bg-surface-muted/60 px-3 py-2.5">
 <input
 type="checkbox"
 checked={!!payForm.via_connect}
 onChange={(e) =>
 setPayForm((f) => ({ ...f, via_connect: e.target.checked, booking_id: e.target.checked ? '' : f.booking_id }))
 }
 className="mt-0.5 w-4 h-4 text-ink border-gray-300 rounded"
 />
 <span>
 <span className="text-sm font-semibold text-ink">
 Pay via Stripe Connect transfer
 </span>
 <span className="block text-xs text-gray-500 mt-0.5">
 Requires Connect transfers enabled in Settlement and a payout-ready vendor account.
 Manual ledger-only recording stays available when unchecked.
 </span>
 </span>
 </label>

 <div>
 <label className="block text-xs font-bold text-ink-secondary uppercase tracking-wider mb-1.5">
 Notes / Payment Reference
 </label>
 <textarea
 value={payForm.notes}
 onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))}
 rows={3}
 className="w-full px-3 py-2 border border-border rounded-xl text-sm bg-surface text-ink focus:ring-2 focus:ring-ink"
 placeholder="Check #, Wire transfer ref, ACH note, billing period..."
 />
 </div>

 <div className="flex gap-2.5 justify-end pt-3 border-t border-border">
 <button
 type="button"
 onClick={() => setPayVendor(null)}
 disabled={recordMutation.isPending}
 className="px-4 py-2.5 rounded-md border border-border text-sm font-semibold text-ink-secondary hover:bg-surface-muted transition-colors disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 type="submit"
 disabled={isPayDisabled}
 className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ink text-white text-sm font-semibold hover:bg-black transition-all disabled:opacity-40 shadow-sm"
 >
 {recordMutation.isPending && (
 <RefreshCw className="w-4 h-4 animate-spin" />
 )}
 {recordMutation.isPending
 ? (payForm.via_connect ? 'Transferring…' : 'Recording…')
 : (payForm.via_connect ? 'Send Connect transfer' : 'Record & Save Payout')}
 </button>
 </div>
 </form>
 </div>
 ) : null}
 </ModalShell>

 {/* Delete Confirmation Modal */}
 <ModalShell
 isOpen={!!deleteDisbursementTarget}
 onClose={() => {
 if (!deleteMutation.isPending) setDeleteDisbursementTarget(null);
 }}
 labelledBy="delete-disbursement-title"
 panelClassName="max-w-md"
 >
 {deleteDisbursementTarget ? (
 <div className="bg-surface rounded-lg shadow-sm border border-border w-full p-6 space-y-4">
 <div className="flex items-center gap-3 text-red-600">
 <div className="p-3 bg-red-100 rounded-xl">
 <Trash2 className="w-6 h-6" />
 </div>
 <div>
 <h3
 id="delete-disbursement-title"
 className="text-lg font-bold text-ink"
 >
 Delete Disbursement
 </h3>
 <p className="text-xs text-ink-tertiary">This action will remove this payout entry.</p>
 </div>
 </div>

 <div className="bg-surface-muted rounded-xl p-3.5 border border-border text-xs space-y-1.5 text-ink-secondary">
 <p>
 <strong>Reference:</strong>{' '}
 <span className="font-mono">{deleteDisbursementTarget.disbursement_reference}</span>
 </p>
 <p>
 <strong>Amount:</strong>{' '}
 <span className="font-bold text-emerald-700">
 {money(deleteDisbursementTarget.amount)}
 </span>
 </p>
 {deleteDisbursementTarget.notes && (
 <p className="truncate">
 <strong>Notes:</strong> {deleteDisbursementTarget.notes}
 </p>
 )}
 </div>

 <p className="text-xs text-ink-secondary">
 Deleting this disbursement will update the vendor’s ledger and restore{' '}
 <strong>{money(deleteDisbursementTarget.amount)}</strong> to their available balance due.
 </p>

 <div className="flex gap-2.5 justify-end pt-3 border-t border-border">
 <button
 type="button"
 onClick={() => setDeleteDisbursementTarget(null)}
 disabled={deleteMutation.isPending}
 className="px-4 py-2 rounded-md border border-border text-xs font-semibold text-ink-secondary hover:bg-surface-muted disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 type="button"
 onClick={() => deleteMutation.mutate(deleteDisbursementTarget.id)}
 disabled={deleteMutation.isPending}
 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
 >
 {deleteMutation.isPending && (
 <RefreshCw className="w-3.5 h-3.5 animate-spin" />
 )}
 {deleteMutation.isPending ? 'Deleting…' : 'Confirm Delete'}
 </button>
 </div>
 </div>
 ) : null}
 </ModalShell>
 </div>
 );
};

export default AdminVendorPayouts;
